import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { useSeo } from '../lib/useSeo';

const Login = () => {
  useSeo({ title: 'Iniciar sesión | TriFlow Pro — Entrenamiento de Triatlón', description: 'Accede a tu cuenta de TriFlow Pro para ver tus entrenamientos de natación, ciclismo y carrera, tu carga de entrenamiento y los mensajes de tu coach.', path: '/login', noindex: true });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const userData = await login(email, password);
      toast.success('¡Bienvenido de vuelta!');
      navigate(userData?.role === 'admin' ? '/admin' : '/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img 
              src="/coach-logo-dark.png" 
              alt="Coach Alex Díaz" 
              className="w-14 h-14 object-contain"
            />
            <span className="font-black text-xl tracking-tight">Coach Alex Díaz</span>
          </Link>

          <div className="mb-8">
            <h1 className="text-3xl font-black mb-2">Iniciar Sesión</h1>
            <p className="text-zinc-400">Continúa con tu entrenamiento</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-dark h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-dark h-12 pr-12"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary h-12 text-base"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar Sesión'
              )}
            </Button>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-zinc-400 hover:text-[#ccff00] hover:underline"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </form>

          <p className="mt-8 text-center text-zinc-400">
            ¿No tienes cuenta?{' '}
            <Link to="/register" className="text-[#ccff00] hover:underline font-medium" data-testid="register-link">
              Regístrate gratis
            </Link>
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#09090b]"></div>
        <img
          src="https://customer-assets.emergentagent.com/job_tri-planner-1/artifacts/cqr035t3_IMG_4594.jpeg"
          alt="Natación triatlón"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#00f0ff]/20 mix-blend-overlay"></div>
      </div>
    </div>
  );
};

export default Login;
